import type {
  AuditEventDto,
  BrandingVersionDto,
  ManagedPreviewSessionDto,
  PreviewTargetDto,
  WorkspaceDto,
  WorkspaceMcpTokenDto,
  WorkspaceRuntimeCredentialDto,
} from "../../api/generated.ts";

export type FilterSelectionMode = "single" | "multiple";
export type SearchValue = string | number | boolean | null | undefined;

export type BrandingVersionDisplayState = BrandingVersionDto["state"] | "archived";
export type WorkspaceMcpTokenDisplayStatus = "active" | "overlap" | "expired" | "revoked";
export type WorkspaceRuntimeCredentialDisplayStatus = "active" | "expired" | "revoked";

interface SearchFilters {
  readonly query?: string;
}

export type WorkspaceListFilters = SearchFilters;

export interface BrandingVersionListFilters extends SearchFilters {
  readonly states?: readonly BrandingVersionDisplayState[];
  readonly sources?: readonly BrandingVersionDto["sourceType"][];
}

export interface PreviewTargetListFilters extends SearchFilters {
  readonly statuses?: readonly PreviewTargetDto["status"][];
}

export interface PreviewSessionListFilters extends SearchFilters {
  readonly statuses?: readonly ManagedPreviewSessionDto["status"][];
  readonly targetIds?: readonly string[];
}

export interface WorkspaceMcpTokenListFilters extends SearchFilters {
  readonly statuses?: readonly WorkspaceMcpTokenDisplayStatus[];
}

export interface WorkspaceRuntimeCredentialListFilters extends SearchFilters {
  readonly statuses?: readonly WorkspaceRuntimeCredentialDisplayStatus[];
  readonly scopes?: readonly WorkspaceRuntimeCredentialDto["scopes"][number][];
}

export interface AuditEventListFilters extends SearchFilters {
  readonly decisions?: readonly AuditEventDto["decision"][];
  readonly actorTypes?: readonly AuditEventDto["actorType"][];
  readonly sourceSystems?: readonly AuditEventDto["sourceSystem"][];
}

function normalizeSearchValue(value: SearchValue): string {
  return value === null || value === undefined ? "" : String(value).trim().toLowerCase();
}

function matchesSelection<T>(selection: readonly T[] | undefined, value: T): boolean {
  return !selection?.length || selection.includes(value);
}

export function matchesNormalizedSearch(query: string | undefined, ...values: readonly SearchValue[]): boolean {
  const normalizedQuery = normalizeSearchValue(query);
  if (!normalizedQuery) return true;
  return values.some((value) => normalizeSearchValue(value).includes(normalizedQuery));
}

export function toggleControlledFilterValue<T extends string>(
  current: readonly T[],
  value: T,
  mode: FilterSelectionMode,
): T[] {
  if (mode === "single") return current.includes(value) ? [] : [value];
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}

export function getBrandingVersionDisplayState(version: BrandingVersionDto): BrandingVersionDisplayState {
  return version.archivedAt ? "archived" : version.state;
}

export function filterWorkspaces(
  workspaces: readonly WorkspaceDto[],
  filters: WorkspaceListFilters,
): WorkspaceDto[] {
  return workspaces.filter((workspace) => matchesNormalizedSearch(
    filters.query,
    workspace.name,
    workspace.slug,
    workspace.id,
    workspace.organizationId,
  ));
}

export function filterBrandingVersions(
  versions: readonly BrandingVersionDto[],
  filters: BrandingVersionListFilters,
): BrandingVersionDto[] {
  return versions.filter((version) => {
    const state = getBrandingVersionDisplayState(version);
    return matchesNormalizedSearch(
      filters.query,
      version.title,
      state,
      version.sourceType,
      version.definitionHash,
      version.createdBy,
      version.sourceTemplateId,
      version.sourceVersionId,
      version.version,
    )
      && matchesSelection(filters.states, state)
      && matchesSelection(filters.sources, version.sourceType);
  });
}

export function filterPreviewTargets(
  targets: readonly PreviewTargetDto[],
  filters: PreviewTargetListFilters,
): PreviewTargetDto[] {
  return targets.filter((target) => matchesNormalizedSearch(
    filters.query,
    target.name,
    target.origin,
    target.callbackPath,
    target.audience,
    target.externalReleaseReference,
    target.status,
  ) && matchesSelection(filters.statuses, target.status));
}

export function filterPreviewSessions(
  sessions: readonly ManagedPreviewSessionDto[],
  filters: PreviewSessionListFilters,
): ManagedPreviewSessionDto[] {
  return sessions.filter((session) => matchesNormalizedSearch(
    filters.query,
    session.draftTitle,
    session.status,
    session.definitionHash,
    session.compiledHash,
    session.targetOrigin,
    session.targetAudience,
    session.targetCallbackPath,
    session.initialModeId,
  )
    && matchesSelection(filters.statuses, session.status)
    && matchesSelection(filters.targetIds, session.targetId));
}

export function getWorkspaceMcpTokenDisplayStatus(
  token: WorkspaceMcpTokenDto,
  now: number | Date,
): WorkspaceMcpTokenDisplayStatus {
  if (token.status === "revoked") return "revoked";
  const timestamp = typeof now === "number" ? now : now.getTime();
  if (
    Date.parse(token.expiresAt) <= timestamp
    || (token.overlapUntil !== null && Date.parse(token.overlapUntil) <= timestamp)
  ) return "expired";
  return token.overlapUntil ? "overlap" : "active";
}

export function filterWorkspaceMcpTokens(
  tokens: readonly WorkspaceMcpTokenDto[],
  filters: WorkspaceMcpTokenListFilters,
  now: number | Date,
): WorkspaceMcpTokenDto[] {
  return tokens.filter((token) => {
    const status = getWorkspaceMcpTokenDisplayStatus(token, now);
    return matchesNormalizedSearch(
      filters.query,
      token.name,
      token.credentialPrefix,
      token.createdBy,
      status,
      token.lastUsedAt,
    ) && matchesSelection(filters.statuses, status);
  });
}

export function getWorkspaceRuntimeCredentialDisplayStatus(
  credential: WorkspaceRuntimeCredentialDto,
  now: number | Date,
): WorkspaceRuntimeCredentialDisplayStatus {
  if (credential.status === "revoked") return "revoked";
  const timestamp = typeof now === "number" ? now : now.getTime();
  return credential.expiresAt && Date.parse(credential.expiresAt) <= timestamp ? "expired" : "active";
}

export function filterWorkspaceRuntimeCredentials(
  credentials: readonly WorkspaceRuntimeCredentialDto[],
  filters: WorkspaceRuntimeCredentialListFilters,
  now: number | Date,
): WorkspaceRuntimeCredentialDto[] {
  return credentials.filter((credential) => {
    const status = getWorkspaceRuntimeCredentialDisplayStatus(credential, now);
    return matchesNormalizedSearch(
      filters.query,
      credential.consumerId,
      credential.credentialPrefix,
      credential.createdBy,
      credential.scopes.join(" "),
      status,
      credential.lastUsedAt,
    )
      && matchesSelection(filters.statuses, status)
      && (!filters.scopes?.length || filters.scopes.some((scope) => credential.scopes.includes(scope)));
  });
}

export function filterAuditEvents(
  events: readonly AuditEventDto[],
  filters: AuditEventListFilters,
): AuditEventDto[] {
  return events.filter((event) => matchesNormalizedSearch(
    filters.query,
    event.action,
    event.decision,
    event.actorType,
    event.actorId,
    event.resourceType,
    event.resourceId,
    event.sourceSystem,
    event.requestId,
    event.correlationId,
    event.workspaceId,
  )
    && matchesSelection(filters.decisions, event.decision)
    && matchesSelection(filters.actorTypes, event.actorType)
    && matchesSelection(filters.sourceSystems, event.sourceSystem));
}
